
// components/OperationHistory.tsx
import React, { useRef, useState, useCallback } from "react";
import {
  Box, Typography, SxProps, Theme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import theme from "../../../style/theme";

export interface ProcessHistoryRow {
  operatedAt: Date | string;
  operation: string;
  comment?: string | null;
  operatorName: string;
  activityName?: string | null;
}

// --- ラベル型 ---
type LabelSet = {
  user: string;
  operation: string;
  comment: string;
  activity: string;
  time: string;
};

// --- props（labels をここで定義）---
interface OperationHistoryProps {
  isMobile: boolean;
  historyList?: ProcessHistoryRow[] | null;
  sortOrder?: "asc" | "desc";      // デフォルト: "desc"
  height?: number;                 // 初期高さ（px）
  maxHeight?: number;              // 最大高さ（px）
  minHeight?: number;              // 最小高さ（px）
  wrap?: boolean;                  // true: 折り返し, false: 省略（…）
  labels?: Partial<LabelSet>;      // ★ ヘッダー文言の上書き用
}

/**
 * 操作履歴テーブルコンポーネント
 * - 申請の操作履歴（操作者・操作内容・コメント・日時）を昇順または降順で表示する
 * - props: isMobile (モバイル表示フラグ), historyList (履歴データ一覧),
 *          sortOrder (ソート順 "asc" | "desc"), labels (ヘッダー文言の上書き用)
 */
export const OperationHistory: React.FC<OperationHistoryProps> = ({
  isMobile,
  historyList,
  sortOrder = "desc",
  height,            // 初期高さ
  maxHeight,
  minHeight,
  wrap = true,
  labels,            // ★ 受け取り
}) => {
  const sorted = (historyList ?? [])
    .slice()
    .sort((a, b) => {
      const atA = a.operatedAt instanceof Date ? a.operatedAt.getTime() : new Date(a.operatedAt).getTime();
      const atB = b.operatedAt instanceof Date ? b.operatedAt.getTime() : new Date(b.operatedAt).getTime();
      return sortOrder === "asc" ? atA - atB : atB - atA;
    });

  const formatJa = (operatedAt: Date | string) => {
    const d = operatedAt instanceof Date ? operatedAt : new Date(operatedAt);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("ja-JP", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  };

  // ---- デフォルトラベル & マージ（L）
  const defaultLabels: LabelSet = {
    user: "実行者",
    operation: "アクション",
    comment: "コメント",
    activity: "対象ステップ",
    time: "実行日時",
  };
  const L: LabelSet = { ...defaultLabels, ...(labels ?? {}) };

  // ---- カラム幅（必要に応じて調整）
  const colW = {
    user: 180,
    op: 120,
    comment: 300,
    activity: 180,
    time: 200,
  };

  // ---- 省略／折返しスタイル
  const ellipsisSx: SxProps<Theme> = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
  const wrapSx: SxProps<Theme> = {
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };

  const cellBase: SxProps<Theme> = {
    verticalAlign: "middle",
    borderBottom: "none",
    py: 0.75,
  };

  const headCell: SxProps<Theme> = {
    fontWeight: 600,
    color: "text.secondary",
    borderBottom: "1px solid #e0e0e0",
    py: 0.75,
    backgroundColor: "background.paper", // sticky時の背景
  };

  // ---- 高さのドラッグ可変
  const DEFAULT_HEIGHT = 150;               // 既定高さ
  const DEFAULT_MAX = isMobile ? 220 : 480;
  const DEFAULT_MIN = 120;

  const [containerHeight, setContainerHeight] = useState<number>(height ?? DEFAULT_HEIGHT);
  const maxH = maxHeight ?? DEFAULT_MAX;
  const minH = minHeight ?? DEFAULT_MIN;

  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(containerHeight);

  const onMouseDownHandle = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    isDraggingRef.current = true;
    startYRef.current = e.clientY;
    startHeightRef.current = containerHeight;

    (e.currentTarget as HTMLDivElement).style.cursor = "ns-resize";

    const onMove = (ev: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const dy = ev.clientY - startYRef.current;
      const next = Math.min(maxH, Math.max(minH, startHeightRef.current + dy));
      setContainerHeight(next);
    };
    const onUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "default";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "ns-resize";
  }, [containerHeight, maxH, minH]);

  const dash = "—";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        mx: isMobile ? 1 : 4,
        my: 1,
        p: "1% 1%",
        position: "center",
        border: "1px solid #ccc",
        borderRadius: "2px",
        backgroundColor: theme.palette.secondary.main,
      }}
    >

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          mt: 2,
        }}
      >
        <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 1, fontWeight: 600 }}>
          操作履歴
        </Typography>

        <Box
          sx={{
            border: "1px solid #bbb",
            borderRadius: 1,
            backgroundColor: "white",
            overflow: "hidden", // 内側でスクロール
            width: "100%",
            maxWidth: "100%",
            margin: "0 auto",               // 中央寄せ
          }}
        >

          <TableContainer
            sx={{
              height: containerHeight,
              overflowY: "auto",
              overflowX: isMobile ? "auto" : "visible",  // ★ RecordDetailと同じ動作に
              WebkitOverflowScrolling: "touch",          // ★ モバイルの慣性スクロール
              scrollbarGutter: "stable",
              width: "100%",
              maxWidth: "100%",
              px: "1%",
            }}
          >

            {sorted.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                履歴はありません
              </Typography>
            ) : (
              <Table
                size={isMobile ? "small" : "medium"}
                stickyHeader
                aria-label="操作履歴テーブル"
                sx={{ tableLayout: "fixed"}}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCell, width: colW.user }}>
                      <Typography sx={wrap ? wrapSx : ellipsisSx}>{L.user}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...headCell, width: colW.op, minWidth: colW.op }}>
                      <Typography sx={wrap ? wrapSx : ellipsisSx}>{L.operation}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...headCell, width: colW.comment }}>
                      <Typography sx={wrap ? wrapSx : ellipsisSx}>{L.comment}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...headCell, width: colW.activity }}>
                      <Typography sx={wrap ? wrapSx : ellipsisSx}>{L.activity}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...headCell, width: colW.time }}>
                      <Typography sx={wrap ? wrapSx : ellipsisSx}>{L.time}</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sorted.map((row, idx) => (
                    <TableRow key={idx}>
                      {/* 実行者 */}
                      <TableCell sx={{ ...cellBase, width: colW.user }}>
                        <Typography
                          variant="body2"
                          sx={wrap ? wrapSx : ellipsisSx}
                          title={wrap ? undefined : row.operatorName}
                        >
                          {row.operatorName || dash}
                        </Typography>
                      </TableCell>

                      {/* アクション（強調＋折り返し） */}
                      <TableCell sx={{ ...cellBase, width: colW.op, minWidth: colW.op }}>
                        <Typography
                          variant="body2"
                          sx={[{ fontWeight: 700 }, wrap ? wrapSx : ellipsisSx]}
                          title={wrap ? undefined : row.operation}
                        >
                          {row.operation || dash}
                        </Typography>
                      </TableCell>

                      {/* メモ */}
                      <TableCell sx={{ ...cellBase, width: colW.comment }}>
                        <Typography
                          variant="body2"
                          sx={wrap ? wrapSx : ellipsisSx}
                          title={wrap ? undefined : (row.comment ?? "")}
                        >
                          {row.comment ?? dash}
                        </Typography>
                      </TableCell>

                      {/* 対象工程 */}
                      <TableCell sx={{ ...cellBase, width: colW.activity }}>
                        <Typography
                          variant="body2"
                          sx={wrap ? wrapSx : ellipsisSx}
                          title={wrap ? undefined : (row.activityName ?? "")}
                        >
                          {row.activityName ?? dash}
                        </Typography>
                      </TableCell>

                      {/* 実行日時 */}
                      <TableCell sx={{ ...cellBase, width: colW.time }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={wrap ? wrapSx : ellipsisSx}
                          title={wrap ? undefined : formatJa(row.operatedAt)}
                        >
                          {formatJa(row.operatedAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          {/* ドラッグハンドル（下辺） */}
          <Box
            onMouseDown={onMouseDownHandle}
            sx={{
              height: 8,
              cursor: "ns-resize",
              bgcolor: "transparent",
              borderTop: "1px solid #e0e0e0",
              "&:hover": { bgcolor: "#f7f7f7" },
            }}
            title={`高さをドラッグで調整`}
          />
        </Box>
      </Box>
    </Box>
  );
};

